import Route from '@ember/routing/route';
import config from '@linc-technologies/linced-common/utils/config';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { signIn } from '@linc-technologies/linced-common/utils/oauth2';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { action } from '@ember/object';

export default class StartupRoute extends Route {
  @service
  logging;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  toaster;

  async beforeModel(transition) {
    if (!sessionStorage.getItem('tabId')) {
      const id = crypto.randomUUID();
      sessionStorage.setItem('tabId', id);
    }

    // A prior /callback attempt may have failed a genuine sign-in (not a
    // stale/reused code) while a different user's session was already
    // active, and left this notice for the next page load to surface since
    // window.location.replace() discards any in-memory toast. See the
    // signInFailed handling in callback().
    const signInFailedKey = 'session:signInFailedNotice';
    if (sessionStorage.getItem(signInFailedKey)) {
      sessionStorage.removeItem(signInFailedKey);
      this.toaster.error(
        'Sign-in failed. You are still signed in as the previous user.',
      );
    }

    const toName = transition?.to?.name;

    // Don't need auth for public routes, just return and let them render
    if (config.APP?.publicRoutes?.includes(toName)) {
      return;
    }

    switch (toName) {
      case 'callback':
        await this.callback(transition);
        // Return regardless of outcome, the callback has handled the transition
        // (abort & home, purge & retry login, or already-authenticated skip).
        // Continuing to the isAuthenticated check below can trigger login()
        // which overrides any ember transition already in progress, causing an
        // infinite loop.
        return;
    }

    // Login if we have no auth.
    if (!(await this.session.isAuthenticated())) {
      await this.login();
      return;
    }

    // Ensure we are ready to go before letting app render.
    await this.session.ensure();

    // Call hook for subclasses to run logic after session is ensured.
    await this.afterEnsure(transition);

    // Check if the authenticated user belongs in this app. Scope
    // requirements are defined in each app's config/environment.js.
    if (this.shouldRedirectToOtherApp()) {
      transition.abort();
      await this.redirectToCorrectApp();
    }
  }

  async login() {
    this.logging.pushEvent('login:signIn');
    await signIn();
  }

  async callback(transition) {
    const wasAuthenticated = await this.session.isAuthenticated();
    const hasCode = new URLSearchParams(window.location.search).has('code');

    // A callback carrying a code is always processed, even when already
    // authenticated: a fresh code must be able to replace the stored session
    // (e.g. the user authenticated again, possibly as someone else). The
    // oauth library falls back to the existing session if the exchange fails
    // (stale code from the back button, or a concurrent tab already won the
    // exchange). Only skip when there is no code to process.
    if (wasAuthenticated && !hasCode) {
      this.logging.pushLog(
        'already authenticated and no code present, skipping callback.',
        'callback:beforeModel',
      );
      // Full page reload to '/' so the complete startup flow runs from
      // scratch, including ensure() and shouldRedirectToOtherApp().
      // Use replace() so /callback is not left in the browser history.
      window.location.replace('/');
      return;
    }

    this.logging.pushEvent(`callback:starting`);

    // Snapshot the stored token before the exchange so the success path below
    // can tell whether it actually replaced the session (see sessionReplaced):
    // a stale/duplicated /callback?code=... URL visited while already
    // authenticated can be a total no-op (exchange skipped, or falls back to
    // the existing session on failure), and treating that as a real
    // replacement would reload every other tab, discarding their in-progress
    // form input, for nothing.
    const tokenBeforeExchange = wasAuthenticated
      ? (await this.session.storedAccessToken())?.token
      : undefined;

    const loginAttemptKey = 'session:loginAttemptCount';
    const maxLoginAttempts = 3;
    try {
      const callbackData = await this.session.doCallback();
      if (isEmpty(callbackData.token)) {
        throw new Error('error exchanging code');
      }
      this.logging.pushEvent('callback:complete');
      // Clear the login attempt counter on success.
      sessionStorage.removeItem(loginAttemptKey);

      // sessionReplaced is true only when tokens actually changed identity:
      // either reauthenticate() flagged it (it purges tokens before
      // redirecting here, so wasAuthenticated reads false even though this
      // callback is replacing a session for other tabs), or the exchange
      // produced a token different from what was already stored.
      // wasAuthenticated alone is too broad; it also matches a stale/no-op
      // callback that left the stored token untouched.
      //
      // The flag lives in localStorage, not sessionStorage: the tab that
      // completes the exchange isn't necessarily the one reauthenticate()
      // started it in. A sibling tab's 'reauth-pending' fallback timeout
      // (see session.js) can reload and race its own independent login()
      // to /callback first, with no sessionStorage memory of why it's
      // there; localStorage lets that tab still see the flag and notify.
      const notifyOtherTabsKey = 'session:notifyOtherTabsOnCallback';
      const sessionReplaced =
        !!localStorage.getItem(notifyOtherTabsKey) ||
        (wasAuthenticated && callbackData.token.token !== tokenBeforeExchange);
      if (sessionReplaced) {
        localStorage.removeItem(notifyOtherTabsKey);
        this.session.notifySessionReplaced();
      }

      // Ensure the session is fully ready before transitioning. ensure() so
      // the home route's beforeModel finds a valid, authenticated session and
      // doesn't kick off another login redirect that races with the model hooks.
      await this.session.ensure();
      // Abort current transition to callback route and redirect to the
      // originally attempted route (encoded in OAuth state), or home.
      transition.abort();

      if (sessionReplaced) {
        // A full reload re-runs the whole startup flow from scratch,
        // including shouldRedirectToOtherApp(): the application route is
        // already active and doesn't re-enter for a same-app Ember
        // transition, so replacing the session in place would skip that
        // check and strand a user who just authenticated as an account
        // belonging to a different app amid permission errors.
        window.location.replace(callbackData.returnUrl || '/');
        return;
      }

      if (callbackData.returnUrl) {
        this.router.replaceWith(callbackData.returnUrl);
      } else {
        this.router.transitionTo('home');
      }
    } catch (error) {
      this.logging.pushError(error, 'callback:beforeModel');
      this.logging.pushEvent('callback:failed');
      transition.abort();

      if (wasAuthenticated) {
        // invalid_grant is the exact response for a definitively dead code
        // (expired, already consumed, e.g. a back-button navigation to an
        // old /callback?code=... URL): the user still has a valid session
        // from before this attempt, and purging/retrying login here would
        // log out an otherwise authenticated user over a code we never
        // needed. Discard it silently and go back into the app on the
        // existing session.
        if (error.oauthErrorCode === 'invalid_grant') {
          window.location.replace('/');
          return;
        }
        // Any other failure means a genuine sign-in attempt failed while a
        // DIFFERENT user's session was already active on this device (e.g. a
        // shared classroom machine, or an expired proxy request). Falling
        // back to the existing session is still correct - the user isn't
        // logged out - but doing it silently would mask a failed login as
        // someone else's account staying active, so surface it instead.
        sessionStorage.setItem('session:signInFailedNotice', '1');
        window.location.replace('/');
        return;
      }

      // Normal callback failure (bad code, network error, etc.), purge stale
      // tokens and retry. Guard against an endless loop with a counter.
      // sessionStorage survives auth server redirects within the same tab.
      await this.session.purgeTokens();
      if (this.session.consumeGuardAttempt(loginAttemptKey, maxLoginAttempts)) {
        await this.login();
      } else {
        this.logging.pushError(
          `login failed after ${maxLoginAttempts} attempts`,
          'callback:beforeModel',
        );
        await this.session.logout('callbackRetryExceeded');
      }
    }
  }

  async afterEnsure(/* transition */) {
    // Override in subclasses to run logic after session is ensured.
  }

  shouldRedirectToOtherApp() {
    const { requiredScope, maxScope } = config.APP;
    if (requiredScope && !this.permissions.meet(requiredScope)) {
      return true;
    }
    if (maxScope && this.permissions.meet(maxScope)) {
      return true;
    }
    // Non-admin apps: a user with no school assigned doesn't belong here.
    if (
      config.modulePrefix !== 'linced-admin' &&
      isEmpty(this.session.schoolId)
    ) {
      return true;
    }
    // Admin app: a user with a school assigned belongs in the teacher app.
    if (
      config.modulePrefix === 'linced-admin' &&
      !isEmpty(this.session.schoolId)
    ) {
      return true;
    }
    return false;
  }

  async redirectToCorrectApp() {
    // Admin app: user has a school — send to teacher app.
    if (config.modulePrefix === 'linced-admin' && config.APP.schoolHost) {
      this.logging.pushEvent('session:hasSchoolId:redirectToTeacher');
      window.location.href = `${config.APP.schoolHost}/login`;
      return;
    }

    // Non-admin apps: user has no school — send to admin if they have perms,
    // otherwise log out.
    if (
      config.modulePrefix !== 'linced-admin' &&
      isEmpty(this.session.schoolId)
    ) {
      const hasAdminPerms = this.permissions.meet(Scopes.LINCED_MANAGER);
      if (hasAdminPerms && config.APP.adminHost) {
        this.logging.pushEvent('session:noSchoolId:redirectToAdmin');
        window.location.href = `${config.APP.adminHost}/login`;
        return;
      }
      this.logging.pushError(
        'logging out: no school ID and no admin perms',
        'redirectToCorrectApp',
      );
      await this.session.logout('noSchoolId');
      return;
    }

    // Redirect chain ordered by role priority (highest first).
    // Each app only defines hosts for OTHER apps in their config,
    // so this naturally skips redirecting to self.
    const redirectChain = [
      {
        scope: 'urn:linced:organisation:role:staff',
        host: config.APP.schoolHost,
      },
      {
        scope: 'urn:linced:organisation:role:caregiver',
        host: config.APP.caregiverHost,
      },
      {
        scope: 'urn:linced:organisation:role:learner',
        host: config.APP.learnerHost,
      },
    ];

    for (const { scope, host } of redirectChain) {
      if (host && this.permissions.meet(scope)) {
        window.location.href = host + '/login';
        return;
      }
    }
  }

  @action
  error(error, transition) {
    this.logging.pushError(error, 'route:error', {
      transitionFrom: transition.from ? transition.from.name : 'unknown',
      transitionTo: transition.to ? transition.to.name : 'unknown',
      code: error.errors?.[0]?.status ?? 'unknown',
      detail: error.errors?.[0]?.detail ?? 'unknown',
      message: error.message ? error.message : '',
    });
    return true;
  }
}
